import { useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";

import UserStatusBadge from "../components/UserStatusBadge";
import ChangePasswordForm from "../components/ChangePasswordForm";

import Button from "../../../components/common/Button";
import Modal from "../../../components/common/Modal";
import Loader from "../../../components/common/Loader";
import ErrorMessage from "../../../components/common/ErrorMessage";

import { useUsers } from "../hooks/useUsers";


const ROLE_CONFIG = {
  ADMIN: {
    label: "Admin",
    className: "role-admin",
  },

  SALES: {
    label: "Sales",
    className: "role-sales",
  },

  AGENT: {
    label: "Agent",
    className: "role-agent",
  },
};

const UserDetailsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  /*
  |--------------------------------------------------------------------------
  | USERS HOOK
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  | useUsers exposes fetchUser(), not getUser().
  |
  */

  const {
    fetchUser,
    changePassword,
    deleteUser,
  } = useUsers();

  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [
    showPasswordModal,
    setShowPasswordModal,
  ] = useState(false);

  const [
    passwordLoading,
    setPasswordLoading,
  ] = useState(false);

  const [
    passwordError,
    setPasswordError,
  ] = useState("");

  const [
    deleteLoading,
    setDeleteLoading,
  ] = useState(false);

  /*
  |--------------------------------------------------------------------------
  | LOAD USER
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let mounted = true;

    const loadUser = async () => {
      try {
        setLoading(true);
        setError("");

        console.log(
          "Loading user:",
          id
        );

        const result = await fetchUser(id);

        console.log(
          "User details:",
          result
        );

        if (mounted) {
          setUser(result);
        }
      } catch (error) {
        console.error(
          "Unable to load user:",
          error
        );

        if (mounted) {
          setError(
            error?.response?.data?.message ||
              "Unable to load user."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (id) {
      loadUser();
    }

    return () => {
      mounted = false;
    };
  }, [id, fetchUser]);

  /*
  |--------------------------------------------------------------------------
  | CHANGE PASSWORD
  |--------------------------------------------------------------------------
  */

  const handlePasswordSubmit = async ({
    newPassword,
  }) => {
    try {
      setPasswordLoading(true);
      setPasswordError("");

      await changePassword(
        id,
        newPassword
      );

      setShowPasswordModal(false);
    } catch (error) {
      console.error(
        "Unable to change password:",
        error
      );

      setPasswordError(
        error?.response?.data?.message ||
          "Unable to change password."
      );

      throw error;
    } finally {
      setPasswordLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | DELETE / DEACTIVATE USER
  |--------------------------------------------------------------------------
  */

  const handleDelete = async () => {
    if (!user) {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to deactivate ${user.name}?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeleteLoading(true);

      await deleteUser(id);

      navigate("/users");
    } catch (error) {
      console.error(
        "Unable to deactivate user:",
        error
      );

      window.alert(
        error?.response?.data?.message ||
          "Unable to deactivate user."
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="user-details-loading">
        <Loader />
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  if (error) {
    return (
      <div className="user-details-error">
        <ErrorMessage message={error} />

        <div className="user-details-back-button">
          <Button
            variant="secondary"
            onClick={() =>
              navigate("/users")
            }
          >
            Back to users
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="user-details-page">

        {/* Header */}

        <div className="user-details-header">

          <div>
            <button
              type="button"
              onClick={() =>
                navigate("/users")
              }
              className="user-details-back-link"
            >
              ← Back to users
            </button>

            <h1 className="user-details-title">
              User details
            </h1>

            <p className="user-details-subtitle">
              View account information and manage
              this user.
            </p>
          </div>

          <div className="user-details-actions">

            <Button
              variant="secondary"
              onClick={() =>
                navigate(
                  `/users/${user.id}/edit`
                )
              }
            >
              Edit user
            </Button>

            <Button
              variant="ghost"
              onClick={() =>
                setShowPasswordModal(true)
              }
            >
              Change password
            </Button>

            <Button
              variant="danger"
              loading={deleteLoading}
              disabled={
                deleteLoading ||
                user.status === "INACTIVE"
              }
              onClick={handleDelete}
            >
              Deactivate
            </Button>

          </div>
        </div>

        {/* Profile Card */}

        <div className="user-profile-card">

          <div className="user-profile-header">

            <div className="user-avatar">
              {user.name
                ?.charAt(0)
                ?.toUpperCase()}
            </div>

            <div className="user-profile-heading">
              <h2>
                {user.name}
              </h2>

              <p>
                {user.email}
              </p>
            </div>

          </div>

          <div className="user-profile-body">

            <div className="user-info-grid">

              <InfoItem
                label="Full name"
                value={user.name}
              />

              <InfoItem
                label="Email"
                value={user.email}
              />

              <InfoItem
                label="Phone"
                value={
                  user.phone ||
                  "Not provided"
                }
              />

              {/* Role */}

              <div className="info-item">

                <p className="info-label">
                  Role
                </p>

                <div className="role-list">

                  {user.roles?.length ? (
                    user.roles.map((role) => {
                      const config =
                        ROLE_CONFIG[role] || {
                          label: role,
                          className:
                            "role-default",
                        };

                      return (
                        <span
                          key={role}
                          className={`role-badge ${config.className}`}
                        >
                          {config.label}
                        </span>
                      );
                    })
                  ) : (
                    <span className="info-empty">
                      No role
                    </span>
                  )}

                </div>

              </div>

              {/* Status */}

              <div className="info-item">

                <p className="info-label">
                  Status
                </p>

                <div className="status-wrapper">
                  <UserStatusBadge
                    status={user.status}
                  />
                </div>

              </div>

              <InfoItem
                label="User ID"
                value={user.id}
                mono
              />

            </div>

          </div>

        </div>

        {/* Account Information */}

        <div className="account-info-card">

          <h3>
            Account information
          </h3>

          <p className="account-info-description">
            Account creation and modification
            details.
          </p>

          <div className="account-info-grid">

            <InfoItem
              label="Created at"
              value={formatDate(
                user.created_at
              )}
            />

            <InfoItem
              label="Last updated"
              value={formatDate(
                user.updated_at
              )}
            />

          </div>

        </div>

      </div>

      {/* Password Modal */}

      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          if (!passwordLoading) {
            setShowPasswordModal(false);
            setPasswordError("");
          }
        }}
        title="Change password"
      >

        <div className="password-modal-description">

          <p>
            Set a new password for{" "}

            <span>
              {user.name}
            </span>
            .
          </p>

        </div>

        <ChangePasswordForm
          onSubmit={
            handlePasswordSubmit
          }
          onCancel={() => {
            if (!passwordLoading) {
              setShowPasswordModal(false);
              setPasswordError("");
            }
          }}
          loading={passwordLoading}
          serverError={passwordError}
        />

      </Modal>
    </>
  );
};

/*
|--------------------------------------------------------------------------
| INFO ITEM
|--------------------------------------------------------------------------
*/

const InfoItem = ({
  label,
  value,
  mono = false,
}) => {
  return (
    <div className="info-item">

      <p className="info-label">
        {label}
      </p>

      <p
        className={
          mono
            ? "info-value info-value-mono"
            : "info-value"
        }
      >
        {value || "—"}
      </p>

    </div>
  );
};

/*
|--------------------------------------------------------------------------
| DATE FORMAT
|--------------------------------------------------------------------------
*/

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(
    "en-IN",
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  );
};

export default UserDetailsPage;